import React from 'react';
import { OverViewCardInfo } from '../OverviewCardInfo';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import {
	AccessAlarmsOutlined,
	DialpadOutlined,
	HourglassBottomOutlined,
	LockClockOutlined,
	RadioButtonCheckedOutlined,
	RecordVoiceOverOutlined,
	VoicemailOutlined,
	RingVolume,
	WarningAmberOutlined,
	Block,
	SmsFailedOutlined,
	SmartToyOutlined,
	CallEnd,
	InterpreterModeOutlined,
	StarOutlined,
} from '@mui/icons-material';
import './TweakAdvancedSettingsInfo.scss';
import { Alert, Badge, Button, Table } from 'antd';
import TweakAdvancedSettingsForm from './TweakAdvancedSettingsForm';
import { RefetchOptions, QueryObserverResult } from '@tanstack/react-query';
import { MODAL_IDS } from 'src/enums/EModal';
import { closeModal, openModal } from '@/store/modal/slice';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { parseBooleanField, safeJSONParse } from '@/utils/helper';
import { User } from '@/types/TAccounts';
import { CustomModal } from '@/components/CustomModal';
import TimeControlForm from './TimeControlForm/TimeControlForm';
import AddPronunciationForm from './AddPronunciationForm/AddPronunciationForm';
import MaxUsageLimitForm from './MaxUsageLimit/MaxUsageLimitForm';
import LearnMore from '@/components/Buttons/LearnMore';
import { TUTORIALS } from 'src/enums/ETutorials';
import { RobotOutlined } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import {
	getResellerUserAdvanceFeaturesGating,
	getResellerUserAdvanceFeaturesVisibility,
} from '@/store/account/selector';
import { getResellerData } from '@/store/reseller/selector';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { EAgentSettings } from 'src/enums/EAgentSettings';

export const TweakAdvancedSettingsInfo: React.FC<{
	data: any;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	debrand: boolean;
	isNoviUser: boolean;
}> = ({ data, refetch, debrand, isNoviUser }) => {
	const dispatch = useAppDispatch();
	const [activeField, setActiveField] = React.useState<string | null>(null);
	const isIdleMessagesForInactivityEnabled = useFeatureFlag(
		FeatureFlag.IDLE_MESSAGES_FOR_INACTIVITY,
	);
	const isTransferredCallRecordingDefaultEnabled =
		data.call_recording_enabled === true &&
		(data.recordTransferredCall === undefined || data.recordTransferredCall === null);

	const hiddenAdvanceItems = useAppSelector(getResellerUserAdvanceFeaturesVisibility) ?? [];
	const gatedAdvanceItems = useAppSelector(getResellerUserAdvanceFeaturesGating) ?? [];

	const currentReseller = useAppSelector(getResellerData);

	const columns = [
		{
			title: 'Word',
			dataIndex: 'word',
			key: 'word',
		},
		{
			title: 'Pronunciation',
			dataIndex: 'pronunciation',
			key: 'pronunciation',
		},
	];

	const timeControlColumns = [
		{
			title: 'Start Time',
			dataIndex: 'start_time',
			key: 'start_time',
			render: (_: any, record: any) => {
				return <div>{record.start_time || 'NA'}</div>;
			},
		},
		{
			title: 'End Time',
			dataIndex: 'end_time',
			key: 'end_time',
			render: (_: any, record: any) => {
				return <div>{record.end_time || 'NA'}</div>;
			},
		},
		{
			title: 'Phone Number',
			dataIndex: 'phone_number',
			key: 'phone_number',
			render: (_: any, record: any) => {
				return <div>{record.phone_number || 'NA'}</div>;
			},
		},
	];

	const handleShowTweakSettingModal = (field: string) => {
		setActiveField(field);
		dispatch(openModal({ modalId: MODAL_IDS.TWEAK_ADVANCE_SETTING_MODAL }));
	};

	const handleEditClick = () => {
		setActiveField(null);
		dispatch(openModal({ modalId: MODAL_IDS.TWEAK_ADVANCE_SETTING_MODAL }));
	};

	const handleConfirm = () => {
		dispatch(closeModal());
	};

	const handleTimeControlClick = () => {
		dispatch(openModal({ modalId: MODAL_IDS.TIME_CONTROL_MODAL }));
	};

	const handlePronunciationGuideClick = () => {
		dispatch(openModal({ modalId: MODAL_IDS.PRONUNCIATION_GUIDE_MODAL }));
	};

	const handleMaxUsageLimitClick = () => {
		dispatch(openModal({ modalId: MODAL_IDS.MAX_USAGE_LIMIT_MODAL }));
	};

	const cartesiaVoiceId = () => {
		try {
			const englishVoiceId =
				data?.english_voice_selected &&
				safeJSONParse(data.english_voice_selected, {}).provider === 'cartesia'
					? safeJSONParse(data.english_voice_selected, {}).voiceId
					: '';
			const otherVoiceId =
				data?.other_language_voice_selected &&
				safeJSONParse(data.other_language_voice_selected, {}).provider === 'cartesia'
					? safeJSONParse(data.other_language_voice_selected, {})?.voiceId
					: '';
			return englishVoiceId || otherVoiceId || '';
		} catch (err) {
			console.error('Attribute does not exists:', err);
			return '';
		}
	};

	const InfoComponent = () => {
		const isFeatureHidden = (key: string) => hiddenAdvanceItems.includes(`ADVANCED.${key}`);
		const isFeatureGated = (key: string) => gatedAdvanceItems.includes(`ADVANCED.${key}`);

		const renderGatedContent = () => (
			<Alert
				message="Feature Locked"
				description="This feature is not available in your current plan. Please upgrade to access it."
				type="warning"
				showIcon
				action={
					<Button
						type="primary"
						onClick={() =>
							dispatch(openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }))
						}
					>
						Upgrade Now
					</Button>
				}
			/>
		);

		const advancedTimeControls = data?.advancedTimeControls
			? JSON.parse(data?.advancedTimeControls)
			: [];
		const advancedTimeControlsInitial = data?.advancedTimeControls
			? JSON.parse(data.advancedTimeControls)
			: [{ start_time: '', end_time: '', phone_number: '' }];

		return (
			<>
				{(currentReseller?.resellerView || !isFeatureHidden('AI_MODEL')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('AI_MODEL') && !currentReseller?.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('aiModel')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="AI Model"
							description="Choose the AI model that powers your receptionist. Different models offer varying levels of capabilities and performance."
							icon={<SmartToyOutlined className="text-blue-700" />}
						/>
						<Badge
							className={`${baseClass}__item_info mt-1 ${data?.aiModel ? 'badge-success' : 'badge-disabled'}`}
							count={data?.aiModel || 'Default'}
						/>
						{isFeatureGated('AI_MODEL') && !currentReseller?.resellerView && renderGatedContent()}
					</div>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('EXTENSION_DIGITS')) && (
					<>
						<div
							className={`${baseClass}__item`}
							onClick={() =>
								isFeatureGated('EXTENSION_DIGITS') && !currentReseller?.resellerView
									? dispatch(
											openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
										)
									: handleShowTweakSettingModal('extensionDigits')
							}
						>
							<LabelWithDescription
								customClassName="w-8/12"
								label="Extension Digits"
								description='You can set up extension codes that your AI Receptionist can handle to reroute the caller. i.e. "press 3 for billing department".'
								icon={<DialpadOutlined className="mr-1 mb-1" />}
							/>
							{isFeatureGated('EXTENSION_DIGITS') &&
								!currentReseller?.resellerView &&
								renderGatedContent()}
						</div>
					</>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('ALLOW_VOICEMAILS')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('ALLOW_VOICEMAILS') && !currentReseller?.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('allowVoicemails')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="Allow Voicemails"
							description="If someone asks to leave a message, the AI Receptionist will collect a voicemail. This message will be shown separately in the voicemail logs."
							icon={<VoicemailOutlined className="mr-1 mb-1" />}
						/>
						<Badge
							className={`${baseClass}__item_info mt-1 ${data?.voicemails_enabled === 'true' || data?.voicemails_enabled === true ? 'badge-success' : 'badge-disabled'}`}
							count={
								data?.voicemails_enabled === 'true' || data?.voicemails_enabled === true
									? 'On'
									: 'Off'
							}
							data-testid="voicemails-badge"
						/>
						{isFeatureGated('ALLOW_VOICEMAILS') &&
							!currentReseller?.resellerView &&
							renderGatedContent()}
					</div>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('CALL_RECORDING')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('CALL_RECORDING') && !currentReseller?.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('callRecording')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="Record Calls"
							description="Control if all the voice calls your Receptionist receives should be recorded for review in your dashboard afterwards."
							icon={<RadioButtonCheckedOutlined className="mr-1 mb-1" />}
						/>
						<Badge
							className={`${baseClass}__item_info mt-1 ${data?.call_recording_enabled === 'true' || data?.call_recording_enabled === true ? 'badge-success' : 'badge-disabled'}`}
							count={
								data?.call_recording_enabled === 'true' || data?.call_recording_enabled === true
									? 'On'
									: 'Off'
							}
						/>
						{isFeatureGated('CALL_RECORDING') &&
							!currentReseller?.resellerView &&
							renderGatedContent()}
					</div>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('TRANSFERRED_CALL_RECORDING')) &&
					(data?.call_recording_enabled === 'true' || data?.call_recording_enabled === true) && (
						<div
							className={`${baseClass}__item`}
							onClick={() =>
								isFeatureGated('TRANSFERRED_CALL_RECORDING') && !currentReseller?.resellerView
									? dispatch(
											openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
										)
									: handleShowTweakSettingModal('recordTransferredCall')
							}
						>
							<LabelWithDescription
								customClassName="w-8/12"
								label="Record Transferred Calls"
								description="Record both the AI interaction and the transferred portion of calls. Recordings are combined into a single file for easy review. This helps monitor call quality and train your AI."
								icon={<RadioButtonCheckedOutlined className="text-blue-700" />}
							/>
							<Badge
								className={`${baseClass}__item_info mt-1 ${
									data?.recordTransferredCall === 'true' ||
									data?.recordTransferredCall === true ||
									isTransferredCallRecordingDefaultEnabled
										? 'badge-success'
										: 'badge-disabled'
								}`}
								count={
									data?.recordTransferredCall === 'true' ||
									data?.recordTransferredCall === true ||
									isTransferredCallRecordingDefaultEnabled
										? 'On'
										: 'Off'
								}
							/>
							{isFeatureGated('TRANSFERRED_CALL_RECORDING') &&
								!currentReseller?.resellerView &&
								renderGatedContent()}
						</div>
					)}

				{(currentReseller.resellerView || !isFeatureHidden('AMBIANT_BACKGROUND_AUDIO')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('AMBIANT_BACKGROUND_AUDIO') && !currentReseller.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('backgroundAudioEnabled')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="Ambient Background Noise"
							description="Enable ambient background noise—such as room sounds or light keyboard typing—to help the caller sense a live receptionist is handling the call."
							icon={<InterpreterModeOutlined className="mr-1 mb-1" />}
						/>
						<Badge
							className={`${baseClass}__item_info mt-1 ${data?.backgroundAudioEnabled === true || data?.backgroundAudioEnabled === undefined ? 'badge-success' : 'badge-disabled'}`}
							count={
								data?.backgroundAudioEnabled === true || data?.backgroundAudioEnabled === undefined
									? 'On'
									: 'Off'
							}
						/>
						{isFeatureGated('AMBIANT_BACKGROUND_AUDIO') &&
							!currentReseller.resellerView &&
							renderGatedContent()}
					</div>
				)}

				{(currentReseller.resellerView ||
					!isFeatureHidden(FeatureFlag.TOGGLE_RATE_CALL_SURVEY)) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated(FeatureFlag.TOGGLE_RATE_CALL_SURVEY) && !currentReseller.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal(EAgentSettings.TOGGLE_RATE_CALL_SURVEY)
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="Toggle Rate the Call Survey"
							description="Enable or disable Rate the Call survey. This survey asks the user to rate the call after it ends."
							icon={<StarOutlined className="mr-1 mb-1" />}
						/>
						<Badge
							className={`${baseClass}__item_info mt-1 ${data?.rateCallSurveyEnabled === true || data?.rateCallSurveyEnabled === undefined ? 'badge-success' : 'badge-disabled'}`}
							count={data?.rateCallSurveyEnabled !== false ? 'Enabled' : 'Disabled'}
						/>
						{isFeatureGated(FeatureFlag.TOGGLE_RATE_CALL_SURVEY) &&
							!currentReseller.resellerView &&
							renderGatedContent()}
					</div>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('ALLOW_SMS_REPLY_INPUT')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('ALLOW_SMS_REPLY_INPUT') && !currentReseller?.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('allowSmsReplyInput')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="SMS Fallback for Voice Recognition"
							description="When the AI has trouble understanding voice input, it will ask the caller to send information via text message. This helps improve communication when voice recognition is challenging."
							icon={<SmsFailedOutlined className="mr-1 mb-1" />}
						/>
						<Badge
							className={`${baseClass}__item_info mt-1 ${data?.allowSmsReplyInput === undefined || data?.allowSmsReplyInput === true ? 'badge-success' : 'badge-disabled'}`}
							count={
								data?.allowSmsReplyInput === undefined || data?.allowSmsReplyInput === true
									? 'On'
									: 'Off'
							}
						/>
						{isFeatureGated('ALLOW_SMS_REPLY_INPUT') &&
							!currentReseller?.resellerView &&
							renderGatedContent()}
					</div>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('HANGUP_WORKFLOW')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('HANGUP_WORKFLOW') && !currentReseller?.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('hangupWorkflow')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="Auto Hangup after interaction ends"
							description="Enable this option to automatically end the call when the interaction is clearly over. This helps avoid unnecessary call duration and reduces additional costs."
							icon={<RingVolume className="mr-1 mb-1" />}
						/>
						<Badge
							className={`${baseClass}__item_info mt-1 ${data?.autoHangupAfterInteraction === true || data?.autoHangupAfterInteraction === undefined ? 'badge-success' : 'badge-disabled'}`}
							count={
								data?.autoHangupAfterInteraction === true ||
								data?.autoHangupAfterInteraction === undefined
									? 'On'
									: 'Off'
							}
						/>
						{isFeatureGated('HANGUP_WORKFLOW') &&
							!currentReseller?.resellerView &&
							renderGatedContent()}
					</div>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('AUTO_HANGUP_SILENCE')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('AUTO_HANGUP_SILENCE') && !currentReseller?.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('autoHangupOnSilenceWorkflow')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="Auto Hangup after silence"
							description="Enable this option to automatically end the call when the caller is silent. This helps avoid unnecessary call duration and reduces additional costs."
							icon={<AccessAlarmsOutlined className="mr-1 mb-1" />}
						/>
						<strong className={`${baseClass}__item_info`}>
							{data?.autoHangupOnSilence
								? data?.autoHangupOnSilence > 3
									? 3
									: data?.autoHangupOnSilence
								: 1}{' '}
							Minutes
						</strong>
						{isFeatureGated('AUTO_HANGUP_SILENCE') &&
							!currentReseller.resellerView &&
							renderGatedContent()}
					</div>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('IDLE_MESSAGES')) &&
					isIdleMessagesForInactivityEnabled && (
						<div
							className={`${baseClass}__item`}
							onClick={() =>
								isFeatureGated('IDLE_MESSAGES') && !currentReseller?.resellerView
									? dispatch(
											openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
										)
									: handleShowTweakSettingModal('autoHangupIdleMessagesWorkflow')
							}
						>
							<LabelWithDescription
								customClassName="w-8/12"
								label="Idle Messages"
								description={
									"Messages that your AI receptionist will say when user hasn't responded."
								}
								icon={<CallEnd className="mr-1 mb-1" />}
							/>

							{isFeatureGated('IDLE_MESSAGES') &&
								!currentReseller?.resellerView &&
								renderGatedContent()}
						</div>
					)}
				{(currentReseller?.resellerView || !isFeatureHidden('TIME_CONTROL')) && (
					<>
						<div
							className={`${baseClass}__item__with-tbl`}
							onClick={() =>
								isFeatureGated('TIME_CONTROL') && !currentReseller?.resellerView
									? dispatch(
											openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
										)
									: handleTimeControlClick()
							}
						>
							<LabelWithDescription
								customClassName="w-8/12"
								label="Time Control"
								description="Define specific times to automatically transfer calls to another number"
								icon={<LockClockOutlined className="mr-1 mb-1" />}
							/>
							<Table
								pagination={false}
								className={`${baseClass}__item__with-tbl_table mt-4 w-8/12`}
								dataSource={advancedTimeControls}
								columns={timeControlColumns}
								scroll={{ y: 120 }}
							/>
							{isFeatureGated('TIME_CONTROL') &&
								!currentReseller?.resellerView &&
								renderGatedContent()}
						</div>
						<CustomModal
							title={
								<>
									<div className="flex items-center gap-4">
										<strong>Time Controls</strong>
										<div>
											<LearnMore tutorialId={TUTORIALS.ACTIVE_TIME_CONTROL} debrand={debrand} />
										</div>
									</div>
								</>
							}
							description="Define specific times to automatically transfer calls to another number"
							children={
								<TimeControlForm
									advancedTimeControls={advancedTimeControlsInitial}
									refetch={refetch}
									time_zone={data?.time_zone || 'UTC'}
								/>
							}
							modalId={MODAL_IDS.TIME_CONTROL_MODAL}
							confirmAction={handleConfirm}
							footer={null}
						/>
					</>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('PRONUNCIATION_GUIDES')) && (
					<>
						<div
							className={`${baseClass}__item__with-tbl`}
							onClick={() =>
								isFeatureGated('PRONUNCIATION_GUIDES') && !currentReseller?.resellerView
									? dispatch(
											openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
										)
									: handlePronunciationGuideClick()
							}
						>
							<LabelWithDescription
								customClassName="w-8/12"
								label="Pronunciation Guides"
								description="Your corresponding pronunciation"
								icon={<RecordVoiceOverOutlined className="mr-1 mb-1" />}
							/>
							<Table
								pagination={false}
								className={`${baseClass}__item__with-tbl_table mt-4 w-8/12`}
								dataSource={data?.word_pronunciation}
								columns={columns}
								scroll={{ y: 120 }}
							/>
							{isFeatureGated('PRONUNCIATION_GUIDES') &&
								!currentReseller?.resellerView &&
								renderGatedContent()}
						</div>
						<CustomModal
							title={
								<div className="flex items-center gap-4">
									<strong>Add Word And Pronunciation</strong>
									<LearnMore tutorialId={TUTORIALS.PRONUNCIATION_GUIDES} debrand={debrand} />
								</div>
							}
							description="Your corresponding pronunciation"
							children={
								<AddPronunciationForm
									word_pronunciation={data?.word_pronunciation || []}
									refetch={refetch}
									voiceId={cartesiaVoiceId()}
								/>
							}
							modalId={MODAL_IDS.PRONUNCIATION_GUIDE_MODAL}
							confirmAction={handleConfirm}
							footer={null}
							width={700}
						/>
					</>
				)}

				{!debrand && (currentReseller?.resellerView || !isFeatureHidden('MAX_USAGE_LIMIT')) && (
					<>
						<div
							className={`${baseClass}__item`}
							onClick={() =>
								isFeatureGated('MAX_USAGE_LIMIT') && !currentReseller?.resellerView
									? dispatch(
											openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
										)
									: handleMaxUsageLimitClick()
							}
						>
							<LabelWithDescription
								customClassName="w-8/12"
								label="Max Usage Limit"
								description="Maximum usage limit (in minutes) for your AI receptionist. Once your usage reaches 50%, 75% or 100% of the limit you will receive an email notification."
								icon={<HourglassBottomOutlined className="mr-1 mb-1" />}
							/>
							<Badge
								className={`${baseClass}__item_info mt-1 ${data?.minutesLimitEnabled ? 'badge-success' : 'badge-disabled'}`}
								count={data?.minutesLimitEnabled ? 'Enabled' : 'Disabled'}
							/>
							{isFeatureGated('MAX_USAGE_LIMIT') &&
								!currentReseller?.resellerView &&
								renderGatedContent()}
						</div>
						<CustomModal
							title={
								<div className="flex items-center gap-4">
									<strong>Set Maximum Usage Limit</strong>
									<LearnMore tutorialId={TUTORIALS.MAX_RECEPTIONIST_MINUTES} />
								</div>
							}
							description="Maximum usage limit (in minutes) for your AI receptionist. Once your usage reaches 50%, 75% or 100% of the limit you will receive an email notification."
							children={
								<MaxUsageLimitForm
									refetch={refetch}
									usage_notification_emails={data?.usage_notification_emails || []}
									max_minutes={data?.max_minutes}
									minutesLimitEnabled={data?.minutesLimitEnabled}
								/>
							}
							modalId={MODAL_IDS.MAX_USAGE_LIMIT_MODAL}
							confirmAction={handleConfirm}
							footer={null}
						/>
					</>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('MAX_CALL_DURATION')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('MAX_CALL_DURATION') && !currentReseller?.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('maxCallDuration')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="Maximum Call Duration"
							description="Maximum call duration (in minutes) of the AI receptionist before it automatically hang up"
							icon={<AccessAlarmsOutlined className="mr-1 mb-1" />}
						/>
						<strong className={`${baseClass}__item_info`}>{data?.maxCallDuration} Minutes</strong>
						{isFeatureGated('MAX_CALL_DURATION') &&
							!currentReseller?.resellerView &&
							renderGatedContent()}
					</div>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('BLOCKED_NUMBERS')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('BLOCKED_NUMBERS') && !currentReseller?.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('blockedNumbers')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="Blocked Numbers"
							description="List of blocked numbers that are restricted from calling your receptionist."
							icon={<Block className="mr-1 mb-1" />}
						/>
						{isFeatureGated('BLOCKED_NUMBERS') &&
							!currentReseller?.resellerView &&
							renderGatedContent()}
					</div>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('PROMPT_LANDLINE')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('PROMPT_LANDLINE') && !currentReseller?.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('promptForLandline')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="Landline Number SMS Prompt"
							description="We cant deliver sms to a landline number. What if a user calls from a landline? Speak a prompt."
							icon={<SmsFailedOutlined className="mr-1 mb-1" />}
						/>
						{isFeatureGated('PROMPT_LANDLINE') &&
							!currentReseller?.resellerView &&
							renderGatedContent()}
					</div>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('ROBO_CALL_NUMBERS')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('ROBO_CALL_NUMBERS') && !currentReseller?.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('roboCallNumbers')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="Robo Call Detection"
							description="Enable robocall detection to block spam calls from reaching your receptionist, reducing unnecessary charges and keeping your billing efficient."
							icon={<RobotOutlined className="mr-1 mb-1 text-xl" />}
						/>
						<Badge
							className={`${baseClass}__item_info mt-1 ${data?.enabledRoboCallDetection ? 'badge-success' : 'badge-disabled'}`}
							count={data?.enabledRoboCallDetection ? 'On' : 'Off'}
						/>
						{isFeatureGated('ROBO_CALL_NUMBERS') &&
							!currentReseller?.resellerView &&
							renderGatedContent()}
					</div>
				)}
				{(currentReseller?.resellerView || !isFeatureHidden('ROBO_SMS_NUMBERS')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('ROBO_SMS_NUMBERS') && !currentReseller?.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('roboSMSNumbers')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="SMS Bot Spammers"
							description="List of numbers detected as SMS bots and blocked from SMS interactions."
							icon={<RobotOutlined className="mr-1 mb-1 text-xl" />}
						/>
						{isFeatureGated('ROBO_SMS_NUMBERS') &&
							!currentReseller?.resellerView &&
							renderGatedContent()}
					</div>
				)}

				{(currentReseller?.resellerView || !isFeatureHidden('TEMPORARY_DISABLE')) && (
					<div
						className={`${baseClass}__item`}
						onClick={() =>
							isFeatureGated('TEMPORARY_DISABLE') && !currentReseller?.resellerView
								? dispatch(
										openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
									)
								: handleShowTweakSettingModal('temperoryDisabled')
						}
					>
						<LabelWithDescription
							customClassName="w-8/12"
							label="Temporary Disable"
							description="Temporarily disable your Receptionist by either setting a default hangup message or specifying a number to automatically forward calls to."
							icon={<WarningAmberOutlined className="mr-1 mb-1" />}
						/>
						<strong className={`${baseClass}__item_info text-red-500`}>Danger zone</strong>
						{isFeatureGated('TEMPORARY_DISABLE') &&
							!currentReseller?.resellerView &&
							renderGatedContent()}
					</div>
				)}
			</>
		);
	};

	const baseClass = 'tweak-advanced-settings-info';
	try {
		return (
			<div className={baseClass}>
				<OverViewCardInfo
					formActionType="Edit"
					formTitle="Tweak Advanced Settings"
					displayFormActionBtn={false}
					info={<InfoComponent />}
					form={
						<TweakAdvancedSettingsForm
							debrand={debrand}
							refetch={refetch}
							isNoviUser={isNoviUser}
							formInitialValues={{
								rateCallSurveyEnabled: data?.rateCallSurveyEnabled ?? true,
								maxCallDuration: data?.maxCallDuration,
								extension_digits: data?.extension_digits
									? safeJSONParse(data.extension_digits, [])
									: [],
								usage_notification_emails: data?.usage_notification_emails || [],
								max_minutes: data?.max_minutes,
								voicemails_enabled: parseBooleanField(data?.voicemails_enabled, true),
								recordVoicemailAfterNSec: data?.recordVoicemailAfterNSec || 3,
								call_recording_enabled: parseBooleanField(data?.call_recording_enabled, true),
								recordTransferredCall: parseBooleanField(data?.recordTransferredCall, true),
								call_memory_enabled: data?.call_memory_enabled || false,
								word_pronunciation: data?.word_pronunciation || [],
								advancedTimeControls: data?.advancedTimeControls
									? safeJSONParse(data.advancedTimeControls, [])
									: [],
								time_zone: data?.time_zone || 'UTC',
								minutesLimitEnabled: data?.minutesLimitEnabled || true,
								autoHangupAfterInteraction:
									data?.autoHangupAfterInteraction === undefined
										? true
										: data?.autoHangupAfterInteraction,
								autoHangupAfterInteractionText: data?.autoHangupAfterInteractionText || '',
								idleMessage: data?.idleMessage || '',
								autoHangupIdleMessage: data?.autoHangupIdleMessage || '',
								disableReceptionistSettings: (() => {
									const settings = safeJSONParse(
										data?.disableReceptionistSettings?.S ??
											data?.disableReceptionistSettings ??
											'{}',
									);
									return {
										disabled: !!settings.disabled || !!settings.disableType,
										disableType: settings.disableType ?? '',
										forwardingNumber: settings.forwardingNumber ?? '',
										hangupMessage: settings.hangupMessage ?? '',
									};
								})(),
								blockedNumbers: data?.blockedNumbers ? safeJSONParse(data.blockedNumbers, []) : [],
								roboSMSBlocklistNumbers: data?.roboSMSBlocklistNumbers
									? safeJSONParse(data.roboSMSBlocklistNumbers, [])
									: [],
								promptForLandline: data?.promptForLandline || '',
								roboCallWhitelistNumbers: data?.roboCallWhitelistNumbers
									? safeJSONParse(data.roboCallWhitelistNumbers, [])
									: [],
								roboCallBlocklistNumbers: data?.roboCallBlocklistNumbers
									? safeJSONParse(data.roboCallBlocklistNumbers, [])
									: [],
								llmTemperature: data?.llmTemperature,
								enabledRoboCallDetection: data?.enabledRoboCallDetection || false,
								aiModel: data?.aiModel || 'gpt-4o',
								aiPrompt: data?.aiPrompt || '',
								interruption_words: data?.interruption_words || 0,
								autoHangupOnSilence: data?.autoHangupOnSilence
									? data?.autoHangupOnSilence > 3
										? 3
										: data?.autoHangupOnSilence
									: 1,
								allowSmsReplyInput:
									data?.allowSmsReplyInput === undefined ? true : data?.allowSmsReplyInput,
								backgroundAudioEnabled:
									data?.backgroundAudioEnabled === undefined ? true : data?.backgroundAudioEnabled,
								backgroundRoomAmbienceSound: data?.backgroundRoomAmbienceSound || 0.7,
								backgroundKeyboardSound: data?.backgroundKeyboardSound || 0.7,
							}}
							activeField={activeField}
						/>
					}
					width={'40%'}
					footer={null}
					modalId={MODAL_IDS.TWEAK_ADVANCE_SETTING_MODAL}
					onEditClick={handleEditClick}
				/>
			</div>
		);
	} catch (err) {
		console.error('Error occured while parsing return object parameters', err);
		return null;
	}
};
